import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { callAIStructured, AIGatewayError } from "./ai-gateway";
import {
  generateEmbedding,
  profileToEmbeddingText,
  cosineSimilarity,
  toPgVector,
  EmbeddingError,
} from "./lib/embeddings";
import { computeRecalibration } from "./lib/recalibration";
import { enforceQuota, incrementUsage, QuotaExceededError } from "./lib/quota";
import { CAPABILITIES } from "@/lib/quotas";

export type MatchDimension = {
  name: "Skills" | "Experience" | "Domain" | "Seniority";
  score: number; // 1-10
  rationale: string;
};

export type MatchResult = {
  score: number;
  verdict: "Strong fit" | "Promising" | "Stretch" | "Weak fit";
  dimensions: MatchDimension[];
  strengths: string[];
  gaps: string[];
  summary: string;
  roleTitle?: string;
  company?: string;
  semanticSimilarity?: number; // 0-1 cosine
  breakdown?: {
    dimensionAvg: number; // 1-10 (avg of 4 LLM dims)
    similarityContribution: number; // 1-10 (similarity mapped)
    mustHaveBonus: number; // points added for matched must-haves
    mustHavePenalty: number; // points subtracted for missing must-haves
    weights: { dimensions: number; similarity: number; mustHave: number };
  };
  recalibration?: {
    penalty: number;
    flaggedSkills: string[];
    rejectionCount: number;
    explanation: string;
  };
};

const RATIONALE_TOOL = {
  name: "save_match_rationale",
  description:
    "Given a candidate profile, a job description, and a pre-computed semantic similarity score, write the qualitative rationale: per-dimension scores, strengths, gaps, and a summary. Be honest and specific.",
  parameters: {
    type: "object",
    properties: {
      verdict: {
        type: "string",
        enum: ["Strong fit", "Promising", "Stretch", "Weak fit"],
      },
      dimensions: {
        type: "array",
        items: {
          type: "object",
          properties: {
            name: {
              type: "string",
              enum: ["Skills", "Experience", "Domain", "Seniority"],
            },
            score: { type: "number" },
            rationale: { type: "string" },
          },
          required: ["name", "score", "rationale"],
          additionalProperties: false,
        },
      },
      strengths: {
        type: "array",
        items: { type: "string" },
        description: "Specific candidate strengths matching the JD.",
      },
      gaps: {
        type: "array",
        items: { type: "string" },
        description: "Concrete missing skills or experience.",
      },
      summary: {
        type: "string",
        description: "2-3 sentence overall assessment.",
      },
      roleTitle: { type: "string" },
      company: { type: "string" },
      mustHaveSkillsMatched: {
        type: "array",
        items: { type: "string" },
        description: "Required skills from JD that the candidate clearly has.",
      },
      mustHaveSkillsMissing: {
        type: "array",
        items: { type: "string" },
        description: "Required skills from JD the candidate is missing.",
      },
      seniorityGap: {
        type: "number",
        description:
          "Seniority delta in years: positive = candidate over-qualified, negative = under-qualified, 0 = match.",
      },
    },
    required: ["verdict", "dimensions", "strengths", "gaps", "summary"],
    additionalProperties: false,
  },
};

type Rationale = {
  verdict: MatchResult["verdict"];
  dimensions: MatchDimension[];
  strengths: string[];
  gaps: string[];
  summary: string;
  roleTitle?: string;
  company?: string;
  mustHaveSkillsMatched?: string[];
  mustHaveSkillsMissing?: string[];
  seniorityGap?: number;
};

/**
 * Combine LLM dimension scores (primary), semantic similarity (tie-breaker),
 * and must-have skill matching (modifier) into a final 1-10 score.
 *
 * Weights: dimensions 60%, similarity 20%, must-have 20%.
 *  - Dimension avg dominates so the number tracks the rationale.
 *  - Similarity adds a small "vibe-check" boost.
 *  - Must-have matched/missing nudges up/down within ±2 points.
 *  - Seniority is already in the LLM dimension, so we don't double-penalise.
 */
function computeFinalScore(
  dimensions: MatchDimension[],
  similarity: number,
  matched: number,
  missing: number,
): {
  score: number;
  dimensionAvg: number;
  similarityContribution: number;
  mustHaveBonus: number;
  mustHavePenalty: number;
} {
  const dimAvg =
    dimensions.length > 0
      ? dimensions.reduce((s, d) => s + Math.max(1, Math.min(10, d.score)), 0) /
        dimensions.length
      : 5;

  const simContribution = 1 + Math.max(0, Math.min(1, similarity)) * 9; // map 0-1 → 1-10

  // Must-have component: start neutral at 5, +0.6 per matched (cap +2.5),
  // -1.0 per missing (cap -2.5), then clamp to [1,10].
  const mustHaveBonus = Math.min(matched * 0.6, 2.5);
  const mustHavePenalty = Math.min(missing * 1.0, 2.5);
  const mustHaveScore = Math.max(1, Math.min(10, 5 + mustHaveBonus - mustHavePenalty));

  const weighted = dimAvg * 0.6 + simContribution * 0.2 + mustHaveScore * 0.2;
  const score = Math.max(1, Math.min(10, Math.round(weighted * 10) / 10));

  return {
    score,
    dimensionAvg: Math.round(dimAvg * 10) / 10,
    similarityContribution: Math.round(simContribution * 10) / 10,
    mustHaveBonus: Math.round(mustHaveBonus * 10) / 10,
    mustHavePenalty: Math.round(mustHavePenalty * 10) / 10,
  };
}

export const scoreMatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { jdText: string; jdId?: string | null }) => {
    if (!input?.jdText || typeof input.jdText !== "string") {
      throw new Error("jdText is required");
    }
    if (input.jdText.length < 20) {
      throw new Error("Job description too short");
    }
    if (input.jdText.length > 30_000) {
      throw new Error("Job description exceeds 30,000 characters");
    }
    if (
      input.jdId !== undefined &&
      input.jdId !== null &&
      (typeof input.jdId !== "string" ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input.jdId))
    ) {
      throw new Error("jdId must be a valid UUID");
    }
    return { jdText: input.jdText, jdId: input.jdId ?? null };
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    // 0. Quota check
    try {
      await enforceQuota(supabase, userId, CAPABILITIES.match_score);
    } catch (e) {
      if (e instanceof QuotaExceededError) throw new Response(e.message, { status: e.status });
      throw e;
    }

    // 1. Load candidate profile (for both embedding and rationale context)
    const { data: candidateRow, error: candidateErr } = await supabase
      .from("candidate_profiles")
      .select(
        "verified_skills, chronological_experience, raw_resume_text, github_username, embedding",
      )
      .eq("user_id", userId)
      .maybeSingle();

    if (candidateErr) {
      throw new Response(`Failed to load profile: ${candidateErr.message}`, {
        status: 500,
      });
    }

    // 2. Get or generate candidate embedding
    let candidateEmbedding: number[] | null = null;
    const existingEmbedding = (candidateRow as { embedding?: unknown } | null)?.embedding;
    if (existingEmbedding) {
      // pgvector returns string like "[0.1,0.2,...]" through PostgREST
      try {
        candidateEmbedding =
          typeof existingEmbedding === "string"
            ? (JSON.parse(existingEmbedding) as number[])
            : (existingEmbedding as number[]);
      } catch {
        candidateEmbedding = null;
      }
    }

    if (!candidateEmbedding && candidateRow) {
      const profileText = profileToEmbeddingText(candidateRow);
      if (profileText.trim()) {
        try {
          candidateEmbedding = await generateEmbedding(profileText);
          // Persist for next time (using raw SQL via rpc would be cleaner; use update instead)
          await supabase
            .from("candidate_profiles")
            .update({
              embedding: toPgVector(candidateEmbedding) as unknown as never,
              embedding_updated_at: new Date().toISOString(),
            })
            .eq("user_id", userId);
        } catch (e) {
          if (e instanceof EmbeddingError && (e.status === 429 || e.status === 402)) {
            throw new Response(e.message, { status: e.status });
          }
          // Non-fatal: continue without semantic boost
          console.warn("Candidate embedding failed:", e);
        }
      }
    }

    // 3. Generate JD embedding
    let jdEmbedding: number[] | null = null;
    try {
      jdEmbedding = await generateEmbedding(data.jdText);
    } catch (e) {
      if (e instanceof EmbeddingError && (e.status === 429 || e.status === 402)) {
        throw new Response(e.message, { status: e.status });
      }
      console.warn("JD embedding failed:", e);
    }

    // 4. Cosine similarity
    const similarity =
      candidateEmbedding && jdEmbedding
        ? cosineSimilarity(candidateEmbedding, jdEmbedding)
        : 0.5; // neutral fallback when embeddings unavailable

    // 5. Get LLM rationale + must-have analysis
    const profileSummary = JSON.stringify(
      {
        skills: candidateRow?.verified_skills ?? [],
        experience: candidateRow?.chronological_experience ?? {},
      },
      null,
      2,
    ).slice(0, 12_000);

    let rationale: Rationale;
    try {
      rationale = await callAIStructured<Rationale>({
        messages: [
          {
            role: "system",
            content:
              "You are a senior technical recruiter. Analyze the candidate against the JD across 4 dimensions: Skills, Experience, Domain, Seniority (each 1-10). Identify must-have skills from the JD that are clearly matched vs. missing. Estimate seniority gap in years (negative = under-qualified). Be specific and honest. The overall fit score is computed separately from semantic similarity + your structured analysis, so focus on accurate per-dimension scoring and specific strengths/gaps.",
          },
          {
            role: "user",
            content: `Job Description:\n${data.jdText}\n\nCandidate Profile:\n${profileSummary}\n\nSemantic similarity (cosine, 0-1): ${similarity.toFixed(3)}`,
          },
        ],
        tool: RATIONALE_TOOL,
      });
    } catch (e) {
      if (e instanceof AIGatewayError) {
        throw new Response(e.message, { status: e.status });
      }
      throw e;
    }

    // 6. Deterministic final score (with recalibration from past rejections)
    const matched = rationale.mustHaveSkillsMatched?.length ?? 0;
    const missing = rationale.mustHaveSkillsMissing?.length ?? 0;
    const breakdownRaw = computeFinalScore(rationale.dimensions, similarity, matched, missing);

    const recalibration = await computeRecalibration(supabase, userId, data.jdText);
    const finalScore = Math.max(
      1,
      Math.min(10, Math.round((breakdownRaw.score - recalibration.penalty) * 10) / 10),
    );

    // Re-derive verdict from final score so it stays consistent
    const verdict: MatchResult["verdict"] =
      finalScore >= 8.5
        ? "Strong fit"
        : finalScore >= 6.5
          ? "Promising"
          : finalScore >= 4.5
            ? "Stretch"
            : "Weak fit";

    const result: MatchResult = {
      score: finalScore,
      verdict,
      dimensions: rationale.dimensions,
      strengths: rationale.strengths,
      gaps: rationale.gaps,
      summary: rationale.summary,
      roleTitle: rationale.roleTitle,
      company: rationale.company,
      semanticSimilarity: similarity,
      breakdown: {
        dimensionAvg: breakdownRaw.dimensionAvg,
        similarityContribution: breakdownRaw.similarityContribution,
        mustHaveBonus: breakdownRaw.mustHaveBonus,
        mustHavePenalty: breakdownRaw.mustHavePenalty,
        weights: { dimensions: 0.6, similarity: 0.2, mustHave: 0.2 },
      },
      recalibration: recalibration.penalty > 0 ? recalibration : undefined,
    };

    // 7. Persist or reuse JD (with embedding)
    let jdRowId: string | null = null;

    if (data.jdId) {
      // Verify the JD belongs to this user before reusing it
      const { data: existing, error: existingErr } = await supabase
        .from("job_descriptions")
        .select("id")
        .eq("id", data.jdId)
        .eq("user_id", userId)
        .maybeSingle();
      if (existingErr) {
        throw new Response(`Failed to load JD: ${existingErr.message}`, { status: 500 });
      }
      if (existing?.id) {
        jdRowId = existing.id;
        // Refresh embedding/title/company on the existing row if we have new data
        const jdUpdate: Record<string, unknown> = {};
        if (jdEmbedding) {
          jdUpdate.embedding = toPgVector(jdEmbedding);
          jdUpdate.embedding_updated_at = new Date().toISOString();
        }
        if (result.roleTitle) jdUpdate.role_title = result.roleTitle;
        if (result.company) jdUpdate.company = result.company;
        if (Object.keys(jdUpdate).length > 0) {
          await supabase
            .from("job_descriptions")
            .update(jdUpdate as never)
            .eq("id", jdRowId)
            .eq("user_id", userId);
        }
      }
    }

    if (!jdRowId) {
      const jdInsert: Record<string, unknown> = {
        user_id: userId,
        jd_text: data.jdText,
        role_title: result.roleTitle ?? null,
        company: result.company ?? null,
      };
      if (jdEmbedding) {
        jdInsert.embedding = toPgVector(jdEmbedding);
        jdInsert.embedding_updated_at = new Date().toISOString();
      }

      const { data: jd, error: jdErr } = await supabase
        .from("job_descriptions")
        .insert(jdInsert as never)
        .select("id")
        .single();

      if (jdErr || !jd) {
        throw new Response(`Failed to save JD: ${jdErr?.message}`, { status: 500 });
      }
      jdRowId = jd.id;
    }

    // 8. Persist score
    const { error: scoreErr } = await supabase.from("match_scores").insert({
      user_id: userId,
      jd_id: jdRowId,
      score: result.score,
      dimensions: result.dimensions as unknown as never,
      strengths: result.strengths as unknown as never,
      gaps: result.gaps as unknown as never,
    });

    if (scoreErr) {
      throw new Response(`Failed to save score: ${scoreErr.message}`, {
        status: 500,
      });
    }

    // Track usage AFTER all writes succeed.
    await incrementUsage(userId, CAPABILITIES.match_score);

    return result;
  });
